import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components/icon/Icon';

import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip';
import { NavItem } from './components/nav-item/NavItem';
import styles from './SideNavigation.module.scss';

export interface SideNavigationProps {
  activeSubKey?: string;
  data: Category;
  className?: string;
  isExpanded?: boolean;
  onSidebarToggle?: () => void;
  expandable?: boolean;
  showSeparator?: boolean;
  createNewButton?: boolean;
}

export interface CategoryItem {
  label: string;
  key: string;
  icon?: ReactNode;
  badge?: ReactNode;
  disabled?: boolean;
  onClick: (item: CategoryItem) => void;
  borderBottom?: boolean;
  notification?: ReactNode;
}

export interface Category {
  id?: string;
  title?: string;
  key: string;
  createNewLabel?: string;
  items: CategoryItem[];
  bottomItems?: CategoryItem[];
  expandable?: boolean;
  showSeparator?: boolean;
  createNewButton?: boolean;
  onClick?: () => void;
}

export const SideNavigation = ({
  activeSubKey,
  data,
  className,
  isExpanded = true,
  onSidebarToggle,
  expandable = true,
  showSeparator = true,
  createNewButton = true,
}: SideNavigationProps) => {
  const { t } = useTranslation('common');
  const createNewLabel = data.createNewLabel;

  return (
    <aside
      className={clsx(styles.asideExpandable, className, {
        [styles.isExpanded]: isExpanded,
      })}
      data-testid="side-navigation"
    >
      <div className={styles.toggleButtonWrapper}>
        {isExpanded && <header className={styles.header}>{data.title}</header>}
        {expandable && (
          <Tooltip
            label={
              isExpanded
                ? t('common:sideNavigation.hideMenu')
                : t('common:sideNavigation.showMenu')
            }
            trigger={TooltipTrigger.Hover}
            placement="right"
          >
            <button
              className={clsx(styles.toggleButton, {
                [styles.isExpanded]: isExpanded,
              })}
              onClick={onSidebarToggle}
              data-testid={'button-toggle'}
            >
              <Icon iconName="ArrowRight" size={16} />
            </button>
          </Tooltip>
        )}
      </div>

      {!isExpanded && createNewButton && (
        <Tooltip
          label={createNewLabel}
          trigger={TooltipTrigger.Hover}
          placement="right"
        >
          <button
            className={styles.actionButton}
            onClick={data.onClick}
            data-testid={'sidebar-add-button'}
          >
            <Icon iconName="Add" size={16} />
          </button>
        </Tooltip>
      )}

      {isExpanded && createNewLabel && createNewButton && (
        <button
          className={styles.actionButtonWrapper}
          onClick={data.onClick}
          data-testid={'sidebar-add-button-expanded'}
        >
          <Icon iconName="Add" size={16} />
          {createNewLabel}
        </button>
      )}

      {showSeparator && <hr className={styles.separator} />}

      <div
        className={clsx(styles.categories, {
          [styles.isExpanded]: isExpanded,
        })}
      >
        {data.items.map(item => {
          return (
            <NavItem
              key={item.key}
              item={item}
              activeSubKey={activeSubKey}
              sideBarExpanded={isExpanded}
            />
          );
        })}
      </div>

      <div
        className={clsx(styles.categories, styles.bottom, {
          [styles.isExpanded]: isExpanded,
        })}
      >
        {data.bottomItems?.map(item => {
          return (
            <NavItem
              key={item.key}
              item={item}
              activeSubKey={activeSubKey}
              sideBarExpanded={isExpanded}
            />
          );
        })}
      </div>
    </aside>
  );
};
